import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaCheck, FaTimes, FaMapMarkerAlt, FaGlobe, FaSearch, FaHistory, FaInfoCircle, FaPhoneAlt, FaEnvelope, FaCalendarAlt, FaDownload, FaEye, FaUsers, FaBullhorn, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import ReporterRaiseVoiceTabs from "./ReporterRaiseVoiceTabs";

const ReporterGetNewRaiseVoice = () => {
  const navigate = useNavigate();
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAd, setselectedAd] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null, adId: null });
  const [rejectReason, setRejectReason] = useState("");
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  // Filter toggles and search state
  const [cityFilter, setCityFilter] = useState(false);
  const [stateFilter, setStateFilter] = useState(false);
  const [allStatesFilter, setAllStatesFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  // Reporter profile data
  const [profileData, setProfileData] = useState(null);
  // Spinner for Approve/Reject actions
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchVoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reporter/get/new/voice`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched new Voices Data:", response);
        if (response.data.data) {
          setVoices(response.data.data);
          console.log("Voices Data :", response.data.data);
        } else {
          setVoices([]);
        }
      } catch (err) {
        setError("Failed to fetch voices");
      } finally {
        setLoading(false);
      }
    };

    //  get reporter data using api
    const fetchProfileData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reporter/get/icard`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log(" this is reporter Profile Data:", response);
        setProfileData(response.data.data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    };

    fetchVoices();
    fetchProfileData();
  }, [refetchTrigger]);

  // Default to city filter once profile data is available
  useEffect(() => {
    if (profileData && profileData.city) {
      setCityFilter(true);
    } else if (profileData && profileData.state) {
      setStateFilter(true);
    } else {
      setAllStatesFilter(true);
    }
  }, [profileData]);

  // Reset page when filters or search term change
  useEffect(() => {
    setCurrentPage(1);
  }, [cityFilter, stateFilter, allStatesFilter, statusFilter, searchTerm]);

  // Handle approve voice (for reporter, this could be a mark as reviewed or similar action)
  const handleApprove = async (id) => {
    console.log("=== FRONTEND REPORTER ACCEPT DEBUG ===");
    console.log("Attempting to accept ad with ID:", id);
    console.log("Token:", localStorage.getItem("token"));

    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/accept/ryv/ad/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("=== FRONTEND ACCEPT SUCCESS ===");
      console.log("Approved Data Response:", response);
      console.log("Response data:", response.data);

      if (response.data && response.data.success) {
        toast.success("Voice accepted successfully");
        setTimeout(() => {
          setShowApproveSpinner(false);
          setRefetchTrigger(!refetchTrigger); // Trigger refetch instead of reload
        }, 1500);
      } else {
        setShowApproveSpinner(false);
        console.log("Response success false:", response.data);
        toast.error(response.data.message || "Failed to accept voice");
      }
    } catch (error) {
      setShowApproveSpinner(false);
      console.error("=== FRONTEND ACCEPT ERROR ===");
      console.error("Approve error:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);

      if (error.response && error.response.data) {
        const errorMessage = error.response.data.message || "Failed to accept voice";
        console.log("Showing error message:", errorMessage);
        toast.error(errorMessage);
      } else if (error.request) {
        console.log("Network error - no response received");
        toast.error("Network error. Please check your connection and try again.");
      } else {
        console.log("Request setup error:", error.message);
        toast.error("Request error. Please try again.");
      }
    }
  };

  // Handle reject voice
  const handleReject = async (id, reason) => {
    console.log("Rejecting voice with ID:", id, "Reason:", reason);

    // Validate reason before making API call
    if (!reason || reason.trim() === "") {
      toast.error("Please provide a reason for rejection");
      return;
    }

    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/reject/ryv/ad/${id}`,
        { rejectionNote: reason.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Rejected Data Response:", response);
      if (response.data && response.data.success) {
        toast.success(response.data.message || "Voice rejected successfully");
        setTimeout(() => {
          setShowRejectSpinner(false);
          setRefetchTrigger(!refetchTrigger); // Trigger refetch instead of reload
        }, 1500);
      } else {
        setShowRejectSpinner(false);
        toast.error(response.data.message || "Failed to reject voice");
      }
    } catch (error) {
      setShowRejectSpinner(false);
      console.error("Reject error:", error);
      if (error.response && error.response.data) {
        toast.error(error.response.data.message || "Failed to reject voice");
      } else {
        toast.error("Network error. Please try again.");
      }
    }
  };

  // Modal confirm handler
  const handleModalConfirm = () => {
    if (modal.action === "approve") {
      handleApprove(modal.adId);
      setModal({ open: false, action: null, adId: null });
    } else if (modal.action === "reject") {
      // Validate reject reason before proceeding
      if (!rejectReason || rejectReason.trim() === "") {
        toast.error("❌ Please provide a reason for rejection before proceeding!");
        // Add visual feedback by focusing on the textarea
        setTimeout(() => {
          const textarea = document.getElementById("rejectReason");
          if (textarea) {
            textarea.focus();
            textarea.style.borderColor = "#ef4444";
          }
        }, 100);
        return;
      }
      handleReject(modal.adId, rejectReason);
      setModal({ open: false, action: null, adId: null });
      setRejectReason("");
    }
  };

  // Modal cancel handler
  const handleModalCancel = () => {
    setModal({ open: false, action: null, adId: null });
    setRejectReason("");
  };

  if (loading) return <div>Loading...</div>;

  // Filtering logic for rendering
  const filterAds = () => {
    let filtered = voices;
    // Only one of these can be active at a time
    if (cityFilter && profileData && profileData.city) {
      filtered = voices.filter((voice) => voice.city === profileData.city);
    } else if (stateFilter && profileData && profileData.state) {
      filtered = voices.filter((voice) => voice.state === profileData.state);
    } else if (allStatesFilter) {
      filtered = voices.filter((voice) => voice.state);
    }
    if (statusFilter === "approved") {
      filtered = filtered.filter((voice) => voice.status === "approved");
    }

    // Enhanced search functionality across multiple fields
    if (searchTerm.trim()) {
      const searchLower = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((voice) => {
        // Search in RYV ID
        const ryvIdMatch = voice._id?.toLowerCase().includes(searchLower);

        // Search in state
        const stateMatch = voice.state?.toLowerCase().includes(searchLower);

        // Search in city
        const cityMatch = voice.city?.toLowerCase().includes(searchLower);

        // Search in residence address
        const addressMatch = (voice.residenceAddress || voice.address || "")
          .toLowerCase()
          .includes(searchLower);

        // Search in description
        const descriptionMatch = (voice.description || "")
          .toLowerCase()
          .includes(searchLower);

        // Search in created date (formatted)
        let dateMatch = false;
        if (voice.createdAt) {
          const d = new Date(voice.createdAt);
          const formattedDate = d.toLocaleDateString("en-GB");
          const formattedTime = d.toLocaleTimeString("en-GB", { hour12: true });
          const fullDateTime = `${formattedDate} ${formattedTime}`;
          dateMatch =
            fullDateTime.toLowerCase().includes(searchLower) ||
            formattedDate.includes(searchLower) ||
            formattedTime.toLowerCase().includes(searchLower);
        }

        // Return true if any field matches
        return (
          ryvIdMatch ||
          stateMatch ||
          cityMatch ||
          addressMatch ||
          descriptionMatch ||
          dateMatch
        );
      });
    }

    return filtered;
  };

  // Pagination logic
  const filteredData = filterAds();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-2 sm:py-6 px-2 sm:px-6">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* High-Impact Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-4"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 rounded-2xl shadow-xl p-4 sm:p-5 text-white">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl shadow-inner shrink-0">
                <FaBullhorn className="text-xl text-blue-200" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-black tracking-tight leading-none uppercase italic">
                  Raise Your Voice
                </h1>
                <p className="text-blue-100 text-[9px] sm:text-xs font-medium opacity-80 mt-1">
                  Verified intelligence from your community.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/20 flex flex-col items-center min-w-[60px]">
                <div className="text-lg font-black leading-none">{filteredData.length}</div>
                <div className="text-[7px] font-black uppercase tracking-tighter opacity-70">Leads</div>
              </div>
              <button
                onClick={() => setRefetchTrigger(!refetchTrigger)}
                className="bg-white text-blue-700 hover:bg-blue-50 h-10 px-4 rounded-xl font-black uppercase text-[10px] tracking-wider transition-all shadow-lg flex items-center gap-2 group shrink-0"
              >
                <FaHistory className={`group-hover:rotate-180 transition-transform duration-500 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <ReporterRaiseVoiceTabs />

      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-10">
        {/* Modern Filter & Search Interface */}

        {/* Dynamic Content Grid */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-slate-500 font-black uppercase tracking-widest text-xs">Scanning the horizon for leads...</p>
            </motion.div>
          ) : currentPageData.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center"
            >
              <div className="bg-slate-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaSearch className="text-slate-200 text-4xl" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">No Voice Requests Found</h3>
              <p className="text-slate-500 max-w-xs mx-auto text-sm">We couldn't find any complaints matching your current filters or location. Try searching for "Universal" or other cities.</p>
            </motion.div>
          ) : (
            <motion.div
              key="grid"
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              {currentPageData.map((voice, idx) => (
                <motion.div
                  layout
                  key={voice._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex overflow-hidden h-36 sm:h-40 group border-l-4 border-l-blue-500"
                >
                  {/* Compact Media - Left Side */}
                  <div className="relative w-28 sm:w-44 h-full bg-slate-900 shrink-0 overflow-hidden">
                    {voice.image ? (
                      <img
                        src={voice.image.startsWith("http") ? voice.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.image.replace(/\\/g, "/")}`}
                        alt="RYV"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : voice.video ? (
                      <video
                        src={voice.video.startsWith("http") ? voice.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.video.replace(/\\/g, "/")}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <FaBullhorn className="text-slate-300 text-2xl" />
                      </div>
                    )}

                    {/* Floating Badge */}
                    <div className="absolute bottom-2 left-2 flex gap-1">
                      <span className={`px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase text-white ${voice.status === 'approved' ? 'bg-emerald-500' : voice.status === 'modified' ? 'bg-indigo-600' : 'bg-amber-500'}`}>
                        {voice.status === 'approved' ? 'Verified' : voice.status === 'modified' ? 'Modified' : 'New'}
                      </span>
                    </div>
                  </div>

                  {/* Dense Content - Right Side */}
                  <div className="p-3 flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="text-slate-900 font-extrabold text-base sm:text-xl leading-tight line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {voice.description || 'Verified Intelligence'}
                      </h3>
                      <span className="text-[10px] font-bold text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full shrink-0">
                        {voice.targetUserType === 'reporter' ? 'Rep' : voice.targetUserType === 'influencer' ? 'Inf' : 'Uni'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[9px] font-bold text-slate-400 uppercase mt-0.5">
                      <span className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded text-[8px] border border-blue-100 font-mono tracking-tighter">ID: {voice._id.slice(-8)}</span>
                      <FaMapMarkerAlt className="text-blue-500 shrink-0 ml-1" /> {voice.city}
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300 shrink-0"></span>
                      <span>{new Date(voice.createdAt).toLocaleDateString('en-GB')}</span>
                    </div>

                    <p className="text-slate-500 text-[11px] sm:text-xs leading-tight mt-2 line-clamp-2 italic opacity-80">
                      "{voice.description || 'No briefing available.'}"
                    </p>

                    <div className="mt-auto flex items-center gap-2 pt-2 border-t border-slate-50">
                      <button
                        onClick={() => setselectedAd(voice)}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all font-black text-[9px] uppercase"
                      >
                        <FaEye size={10} /> View
                      </button>
                      <button
                        onClick={() => setModal({ open: true, action: "approve", adId: voice._id })}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all font-black text-[9px] uppercase"
                      >
                        <FaCheck size={10} /> Accept
                      </button>
                      <button
                        onClick={() => setModal({ open: true, action: "reject", adId: voice._id })}
                        className="p-1.5 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all"
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Improved Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center pt-10 pb-20">
            <div className="inline-flex items-center gap-3 bg-white/50 backdrop-blur-md px-6 py-3 rounded-full border border-white shadow-xl">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all ${currentPage === 1 ? 'text-slate-300' : 'text-blue-600 hover:bg-blue-50 active:scale-90'}`}
              >
                ←
              </button>

              <div className="flex items-center gap-2 overflow-x-auto max-w-[200px] sm:max-w-none no-scrollbar">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-full font-black text-xs sm:text-sm transition-all ${currentPage === i + 1 ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-110' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all ${currentPage === totalPages ? 'text-slate-300' : 'text-blue-600 hover:bg-blue-50 active:scale-90'}`}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Details Modal */}
      <AnimatePresence>
        {selectedAd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-md z-50 p-2 sm:p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col relative"
            >
              {/* Modal Header */}
              <div className="p-6 sm:p-8 flex items-center justify-between border-b border-slate-50 sticky top-0 bg-white/80 backdrop-blur-xl z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                    <FaInfoCircle size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight uppercase">Voice Dossier</h2>
                    <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      <span>Status: {selectedAd.status}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                      <span>ID: {selectedAd._id.slice(-8)}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setselectedAd(null)}
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-all"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-8 custom-scrollbar space-y-10">
                {/* Media Section */}
                <div className="rounded-[2rem] overflow-hidden bg-slate-900 aspect-video relative group border-4 border-slate-50 shadow-inner">
                  {selectedAd.image ? (
                    <img
                      src={selectedAd.image.startsWith("http") ? selectedAd.image : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.image.replace(/\\/g, "/")}`}
                      className="w-full h-full object-cover"
                      alt="Dossier Media"
                    />
                  ) : selectedAd.video ? (
                    <video
                      src={selectedAd.video.startsWith("http") ? selectedAd.video : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.video.replace(/\\/g, "/")}`}
                      className="w-full h-full object-cover"
                      controls
                    />
                  ) : null}
                </div>

                {/* Info Blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-8">
                    <section>
                      <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-4">Description of Incident</h4>
                      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 border border-slate-100 text-slate-600 italic font-medium leading-relaxed">
                        "{selectedAd.description || "No textual briefing provided."}"
                      </div>
                    </section>
                  </div>

                  <div className="space-y-6">
                    <section className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
                      <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-4">Location Meta</h4>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <FaMapMarkerAlt className="text-rose-500" />
                          <span className="font-bold text-slate-700">{selectedAd.city}, {selectedAd.state}</span>
                        </div>
                        <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded-xl border border-slate-100">
                          {selectedAd.residenceAddress || selectedAd.address || "Address not specified"}
                        </div>
                      </div>
                    </section>

                    <section className="bg-slate-900 rounded-3xl p-6 text-white shadow-xl">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] mb-4">Source Intel</h4>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-blue-400">
                          <FaUsers size={18} />
                        </div>
                        <span className="font-bold">{selectedAd.name || "Verified Source"}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/5 p-3 rounded-xl border border-white/10 flex flex-col">
                          <span className="text-[8px] uppercase text-slate-400 mb-1">Created</span>
                          <span className="text-xs font-bold font-mono tracking-tighter">{new Date(selectedAd.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="bg-white/5 p-3 rounded-xl border border-white/10 flex flex-col">
                          <span className="text-[8px] uppercase text-slate-400 mb-1">Target</span>
                          <span className="text-xs font-bold tracking-tighter">{selectedAd.targetUserType}</span>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-6 sm:p-8 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => { setModal({ open: true, action: "approve", adId: selectedAd._id }); setselectedAd(null); }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-emerald-200 transition-all flex items-center justify-center gap-3 active:scale-95"
                >
                  <FaCheck /> Accept Intelligence
                </button>
                <button
                  onClick={() => { setModal({ open: true, action: "reject", adId: selectedAd._id }); setselectedAd(null); }}
                  className="flex-1 bg-white text-rose-600 border border-rose-100 hover:bg-rose-50 py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-sm transition-all flex items-center justify-center gap-3 active:scale-95"
                >
                  <FaTimes /> Decline Task
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modern Confirmation Modal */}
      <AnimatePresence>
        {modal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[2rem] shadow-2xl p-8 w-full max-w-sm text-center border border-slate-100"
            >
              <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg ${modal.action === 'approve' ? 'bg-emerald-100 text-emerald-600 shadow-emerald-100' : 'bg-rose-100 text-rose-600 shadow-rose-100'
                }`}>
                {modal.action === 'approve' ? <FaCheck size={32} /> : <FaTimes size={32} />}
              </div>

              <h2 className="text-2xl font-black text-slate-800 mb-3 uppercase tracking-tight">
                {modal.action === 'approve' ? 'Confirm Action' : 'Decline Voice'}
              </h2>

              <p className="text-slate-500 text-sm mb-8 font-medium">
                {modal.action === 'approve'
                  ? 'Ready to take on this task? Once accepted, you can track progress and submit proof in your dashboard.'
                  : 'Please state the reason for declining this request. This helps the user understand your decision.'}
              </p>

              {modal.action === 'reject' && (
                <div className="mb-8 text-left">
                  <textarea
                    placeholder="Brief reason for declining..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-sm focus:bg-white focus:ring-4 focus:ring-rose-500/10 transition-all outline-none resize-none font-medium text-slate-700"
                    rows={4}
                  />
                  {!rejectReason.trim() && <p className="text-rose-500 text-[10px] font-black uppercase mt-2 ml-1">Reason is required</p>}
                </div>
              )}

              <div className="space-y-3">
                <button
                  onClick={handleModalConfirm}
                  disabled={modal.action === 'reject' && !rejectReason.trim()}
                  className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 active:scale-95 ${modal.action === 'approve'
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 hover:bg-blue-700'
                    : (rejectReason.trim() ? 'bg-rose-600 text-white shadow-lg shadow-rose-200 hover:bg-rose-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed')
                    }`}
                >
                  Confirm Action
                </button>
                <button
                  onClick={handleModalCancel}
                  className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Go Back
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ultra-Modern Spinner Overlays */}
      <AnimatePresence>
        {(showApproveSpinner || showRejectSpinner) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-white/90 backdrop-blur-xl z-[100]"
          >
            <div className="flex flex-col items-center">
              <div className="relative w-24 h-24 mb-6">
                <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                <div className={`absolute inset-0 border-4 rounded-full border-t-transparent animate-spin ${showApproveSpinner ? 'border-emerald-600' : 'border-rose-600'}`}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`w-12 h-12 rounded-2xl animate-pulse ${showApproveSpinner ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}></div>
                </div>
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-800 animate-pulse">
                Encrypting Response...
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReporterGetNewRaiseVoice;
